<template>
    <rested-card v-if="(isSpaceArchived || isSpaceRested) && isDevelopment"></rested-card>
    <v-card v-else flat :disabled="isSpaceResting" color="transparent" class="w-100 align-self-start">
        <div class="d-flex">
            <div>
                <v-card-title class="secondary--text">Applications</v-card-title>
            </div>
            <v-spacer></v-spacer>
        </div>
        <v-card-text>
            <v-skeleton-loader v-if="applicationsFetching && fetchingNodePools" class="mx-auto" type="table"></v-skeleton-loader>
            <template v-else>
                <div class="d-flex align-center mb-4">
                    <v-btn
                        v-if="(isDevelopment && isSpaceAdmin) || (isDevelopment && currentSpaceType !== spaceTypes.EDUCATION_SPACE && isInstanceEditor)"
                        :to="{
                            name: 'snapshot-applications-add',
                            params: { oid: $route.params.oid, sid: $route.params.sid, iid: $route.params.iid, snid: $route.params.snid }
                        }"
                        color="primary"
                        :disabled="applicationsFetching || isSpaceArchived || isSpaceInAnyRestStateExceptPreResting"
                        class="mr-2">
                        <v-icon left>add</v-icon>
                        Application
                    </v-btn>
                    <v-dialog v-else v-model="AddAppdialog" max-width="500" persistent>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                v-on="on"
                                color="primary"
                                class="mr-2"
                                :disabled="applicationsFetching || isSpaceArchived || isSpaceInAnyRestStateExceptPreResting">
                                <v-icon left>add</v-icon>
                                Application
                            </v-btn>
                        </template>
                        <v-card>
                            <v-card-title class="flex-nowrap align-start">
                                <span class="secondary--text">Insufficient privileges</span>

                                <v-spacer></v-spacer>
                                <v-btn @click="AddAppdialog = false" icon><v-icon>clear</v-icon></v-btn>
                            </v-card-title>

                            <v-card-text class="mt-1">
                                <p>
                                    New applications can be added only by course admins. If you require an additional application for your work, please ask a
                                    course admin to add the application. As a reference, they can do it at the current URL:
                                </p>
                                <div class="d-flex align-center justify-space-between mt-2">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <span class="secondary--text text-truncate" v-on="on">{{ currentURL.substring(0, 100) }}...</span>
                                        </template>
                                        <span>{{ currentURL }}</span>
                                    </v-tooltip>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <div v-on="on">
                                                <copy-to-clipboard
                                                    :textToCopy="currentURL"
                                                    buttonColor="secondary"
                                                    :iconButton="true"
                                                    :isSmall="true"></copy-to-clipboard>
                                            </div>
                                        </template>
                                        <span>Copy to clipboard</span>
                                    </v-tooltip>
                                </div>
                            </v-card-text>

                            <v-card-actions></v-card-actions>
                        </v-card>
                    </v-dialog>
                    <v-btn v-if="isInstanceEditor" color="white" :to="{ name: 'snapshot-images' }" class="mr-2">
                        <v-icon left>mdi-export</v-icon>
                        Exported Applications
                    </v-btn>
                    <v-btn
                        v-if="selected.length > 0 && !isTrialSpace && applicationsWithStatus.length"
                        :disabled="containsDeletingApps(selected)"
                        @click="addToStaging(selected)"
                        color="white"
                        class="mr-2">
                        <v-icon left>mdi-share-variant-outline</v-icon>
                        Stage selected ({{ selected.length }})
                    </v-btn>

                    <v-menu offset-x nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" color="secondary" text class="mr-2" :disabled="isDevelopment && isSpaceRested">
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <v-list-item
                                :disabled="isTrialSpace || applicationsWithStatus.length === 0 || containsDeletingApps(applicationsWithStatus)"
                                @click="addToStaging(applicationsWithStatus)">
                                <v-list-item-icon>
                                    <v-icon
                                        :color="
                                            isTrialSpace || applicationsWithStatus.length === 0 || containsDeletingApps(applicationsWithStatus)
                                                ? null
                                                : 'secondary'
                                        "
                                        :class="
                                            isTrialSpace || applicationsWithStatus.length === 0 || containsDeletingApps(applicationsWithStatus)
                                                ? 'text--disabled'
                                                : ''
                                        ">
                                        mdi-share-variant-outline
                                    </v-icon>
                                </v-list-item-icon>
                                <v-list-item-title
                                    :class="[
                                        isTrialSpace || applicationsWithStatus.length === 0 || containsDeletingApps(applicationsWithStatus)
                                            ? 'text--disabled'
                                            : 'secondary--text',
                                        'mr-5'
                                    ]">
                                    Stage all ({{ applicationsWithStatus.length }})
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="!isTrialSpace && applicationsWithStatus.length"
                                :disabled="selected.length === 0 || containsDeletingApps(selected)"
                                @click="addToStaging(selected)">
                                <v-list-item-icon>
                                    <v-icon
                                        :color="selected.length === 0 || containsDeletingApps(selected) ? null : 'secondary'"
                                        :class="selected.length === 0 || containsDeletingApps(selected) ? 'text--disabled' : ''">
                                        mdi-share-variant-outline
                                    </v-icon>
                                </v-list-item-icon>
                                <v-list-item-title
                                    :class="[selected.length === 0 || containsDeletingApps(selected) ? 'text--disabled' : 'secondary--text', 'mr-5']">
                                    Stage selected ({{ selected.length }})
                                </v-list-item-title>
                            </v-list-item>
                            <v-divider v-if="isDevelopment && (isSpaceAdmin || isInstanceEditor)" class="my-2"></v-divider>
                            <template v-if="isSpaceAdmin || isInstanceEditor">
                                <app-delete-dialog
                                    v-if="isDevelopment"
                                    :selected="selected"
                                    :disabled="selected.length === 0 || containsDeletingApps(selected)"
                                    @finished="selected = []">
                                    <v-list-item :disabled="selected.length === 0 || containsDeletingApps(selected)" e2e-app-delete-for-me>
                                        <v-list-item-icon>
                                            <v-icon
                                                :color="selected.length === 0 || containsDeletingApps(selected) ? null : 'error'"
                                                :class="selected.length === 0 || containsDeletingApps(selected) ? 'text--disabled' : ''">
                                                mdi-trash-can-outline
                                            </v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title
                                            :class="[selected.length === 0 || containsDeletingApps(selected) ? 'text--disabled' : 'error--text', 'mr-5']">
                                            {{ getDeleteLabel }}
                                        </v-list-item-title>
                                    </v-list-item>
                                </app-delete-dialog>
                                <template v-if="currentSpaceType !== spaceTypes.RESEARCH_SPACE && isSpaceAdmin">
                                    <app-delete-dialog
                                        v-if="isMasterInstance || isDistributedInstance"
                                        :selected="selected"
                                        :mode="2"
                                        :disabled="selected.length === 0 || containsDeletingApps(selected)"
                                        @finished="selected = []">
                                        <v-list-item :disabled="selected.length === 0 || containsDeletingApps(selected)" e2e-app-delete-for-others>
                                            <v-list-item-icon>
                                                <v-icon
                                                    :color="selected.length === 0 || containsDeletingApps(selected) ? null : 'error'"
                                                    :class="selected.length === 0 || containsDeletingApps(selected) ? 'text--disabled' : ''">
                                                    mdi-trash-can-outline
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title
                                                :class="[selected.length === 0 || containsDeletingApps(selected) ? 'text--disabled' : 'error--text', 'mr-5']">
                                                {{ getDeleteForOthersLabel }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </app-delete-dialog>
                                    <app-delete-dialog
                                        v-if="isDevelopment && isMasterInstance"
                                        :selected="selected"
                                        :mode="1"
                                        :disabled="selected.length === 0 || containsDeletingApps(selected)"
                                        @finished="selected = []">
                                        <v-list-item :disabled="selected.length === 0 || containsDeletingApps(selected)" e2e-app-delete-for-all>
                                            <v-list-item-icon>
                                                <v-icon
                                                    :color="selected.length === 0 || containsDeletingApps(selected) ? null : 'error'"
                                                    :class="selected.length === 0 || containsDeletingApps(selected) ? 'text--disabled' : ''">
                                                    mdi-trash-can-outline
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title
                                                :class="[selected.length === 0 || containsDeletingApps(selected) ? 'text--disabled' : 'error--text', 'mr-5']">
                                                Delete for all
                                            </v-list-item-title>
                                        </v-list-item>
                                    </app-delete-dialog>
                                </template>
                            </template>
                        </v-list>
                    </v-menu>
                    <v-spacer></v-spacer>
                    <v-text-field
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense
                        placeholder="Search for an application..."
                        v-model="search"
                        clearable
                        class="mr-2"
                        @click:clear="removeSearchQuery"></v-text-field>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn @click="refresh()" icon v-on="on" :loading="applicationsFetching">
                                <v-icon>refresh</v-icon>
                            </v-btn>
                        </template>
                        <span>Refresh Applications</span>
                    </v-tooltip>
                </div>
                <v-data-table
                    :search="search"
                    show-select
                    item-key="aid"
                    :headers="headersFiltered"
                    v-model="selected"
                    :options="tableOptions"
                    :items="applicationsWithStatus"
                    :footer-props="{ 'items-per-page-options': itemsPerPageOptions }">
                    <template v-slot:top="{ pagination, options, updateOptions }">
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </template>
                    <template v-slot:no-data>
                        <span>No applications found. Consider adding a new application via the "+ Application" button above.</span>
                    </template>
                    <template v-slot:[`item.icon`]="{ item }">
                        <div class="d-flex">
                            <img width="30" height="30" :src="appIcon(item)" />
                        </div>
                    </template>
                    <template v-slot:[`item.long_id`]="{ item }">
                        <template v-if="isSpaceInAnyRestState">
                            <v-btn
                                v-if="(isSpacePreResting || isSpaceRested) && isDevelopment"
                                :disabled="item.status === 'Deleting'"
                                text
                                @click="openApp(item.aid)"
                                class="text-none">
                                {{ item.long_id }}
                                <v-icon right color="primary" class="showOnHover">mdi-play-circle-outline</v-icon>
                            </v-btn>
                            <span v-else>
                                {{ item.long_id }}
                            </span>
                        </template>
                        <template v-else>
                            <v-btn
                                v-if="isDevelopment && !isInstanceArchived"
                                :disabled="item.status === 'Deleting'"
                                text
                                @click="openApp(item.aid)"
                                class="text-none">
                                {{ item.long_id }}
                                <v-icon right color="primary" class="showOnHover">mdi-play-circle-outline</v-icon>
                            </v-btn>
                            <div v-else>
                                <span>
                                    {{ item.long_id }}
                                </span>
                                <v-tooltip bottom v-if="isInstanceArchived">
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            class="ml-1"
                                            small
                                            icon
                                            :to="{
                                                name: 'instance-snapshots',
                                                params: {
                                                    oid: $route.params.oid,
                                                    sid: $route.params.sid,
                                                    iid: $route.params.iid,
                                                    snid: $route.params.snid
                                                }
                                            }">
                                            <v-icon v-on="on">info</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>This instance is archived, please restore a snapshot first</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </template>
                    <template v-slot:[`item.size`]="{ item }">
                        <app-scale-info-menu :app="item">
                            <v-btn :disabled="item.status === 'Deleting'" text :to="getScaleRoute(item)" class="text-none secondary--text">
                                <template v-if="switchCloudEnabled && getCurrentSwitchSelection(item)">
                                    <img src="@/assets/switch-favicon.svg" alt="Switch" width="16" height="16" class="mr-2 ml-n1" />
                                    <v-divider vertical></v-divider>
                                    <v-icon left class="ml-2" color="purple" v-if="getSwitchSelectionFamily(getCurrentSwitchSelection(item)) === 'cpu'">
                                        mdi-memory
                                    </v-icon>
                                    <v-icon left class="ml-2" color="light-green" v-if="getSwitchSelectionFamily(getCurrentSwitchSelection(item)) === 'gpu'">
                                        mdi-memory mdi-rotate-45
                                    </v-icon>
                                    <v-icon left class="ml-2" color="orange" v-if="getSwitchSelectionFamily(getCurrentSwitchSelection(item)) === 'memory'">
                                        mdi-nas
                                    </v-icon>
                                    {{ `${getNodepoolPrimaryParameter(getCurrentSwitchSelection(item))}` }}
                                </template>
                                <template v-else-if="item.active_resource === 'ncu'">
                                    <v-icon left color="primary">mdi-card-bulleted-outline</v-icon>
                                    {{ `${item.ncu} CU` }}
                                </template>
                                <template v-else>
                                    <v-icon left color="purple" v-if="getNodepoolFamily(currentNodePool(item.node_pool)) === 'cpu'">mdi-memory</v-icon>
                                    <v-icon left color="light-green" v-if="getNodepoolFamily(currentNodePool(item.node_pool)) === 'gpu'">
                                        mdi-memory mdi-rotate-45
                                    </v-icon>
                                    <v-icon left color="orange" v-if="getNodepoolFamily(currentNodePool(item.node_pool)) === 'ssd'">mdi-nas</v-icon>
                                    {{ `${getNodepoolPrimaryParameter(currentNodePool(item.node_pool))}` }}
                                </template>
                                <v-icon v-if="!(currentSpaceType === spaceTypes.EDUCATION_SPACE && !isSpaceAdmin)" right class="showOnHover">settings</v-icon>
                            </v-btn>
                        </app-scale-info-menu>
                    </template>
                    <template v-slot:[`item.active_resource`]="{ item }">
                        {{ item.active_resource === 'ncu' ? '-' : `${currentNodePool(item.node_pool).credits_per_hour.toFixed(2)}` }}
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                        <v-btn v-if="item.status === 'Stopped'" @click="openApp(item.aid)" fab x-small dark color="primary">
                            <v-icon small>power_settings_new</v-icon>
                        </v-btn>
                        <stop-application
                            v-if="item.status === 'Starting' || item.status === 'Stopping' || (pushUiEnabled && item.status === 'Unavailable')"
                            :app="item"
                            :appName="item.long_id"
                            :isShared="item.shared === 'true'">
                            <v-btn
                                fab
                                x-small
                                dark
                                :color="pushUiEnabled && item.statusInfo && item.statusInfo.color ? item.statusInfo.color : 'deep-orange lighten-2'">
                                <v-icon v-if="pushUiEnabled && item.status === 'Unavailable'" small>mdi-alert</v-icon>
                                <v-progress-circular v-else size="18" color="white" indeterminate />
                            </v-btn>
                        </stop-application>
                        <v-tooltip v-if="pushUiEnabled && item.statusInfo && item.statusInfo.label" bottom max-width="420">
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                    v-bind="attrs"
                                    v-on="on"
                                    x-small
                                    role="status"
                                    aria-live="polite"
                                    :color="item.statusInfo.color"
                                    text-color="white"
                                    class="ml-2 flex-shrink-0"
                                    :class="{ 'status-pulse': item.statusInfo.pulse }">
                                    {{ item.statusInfo.label }}
                                </v-chip>
                            </template>
                            <span>
                                <template v-if="item.failure_container">
                                    Container: {{ item.failure_container }}
                                    <br />
                                </template>
                                <template v-if="item.failure_message">{{ item.failure_message }}</template>
                                <template v-else>{{ item.statusInfo.label }}</template>
                            </span>
                        </v-tooltip>
                        <v-btn v-if="item.status === 'Deleting'" fab x-small dark color="blue-grey lighten-1">
                            <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                        <stop-application v-if="item.status === 'Running'" :app="item" :appName="item.long_id" :isShared="item.shared === 'true'">
                            <v-btn fab x-small dark color="success">
                                <v-icon small>power_settings_new</v-icon>
                            </v-btn>
                        </stop-application>
                    </template>
                    <template v-slot:[`item.description`]="{ item }">
                        <version-description-viewer
                            :name="item.long_id"
                            :iconUrl="item.icon_url"
                            :descriptionUrl="item.image_description"
                            v-if="item.image_description">
                            <a class="subtitle-2">{{ item.description }}</a>
                        </version-description-viewer>
                        <span v-else class="subtitle-2 secondary--text">{{ item.description }}</span>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <div class="d-flex flex-nowrap justify-end">
                            <v-btn
                                v-if="!isTrialSpace && !(isDevelopment && isSpaceRested)"
                                @click="addToStaging([item])"
                                color="secondary"
                                class="mr-2 showOnHover"
                                small
                                outlined
                                text
                                :disabled="item.status === 'Deleting'">
                                <v-icon left>mdi-share-variant-outline</v-icon>
                                stage
                            </v-btn>
                            <v-menu v-model="dropdownMenu[`app_${item.aid}`]" left offset-x nudge-top="10">
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        v-on="on"
                                        text
                                        color="secondary"
                                        :disabled="
                                            (isDistributedInstance && !isSpaceAdmin) ||
                                            isSpaceRested ||
                                            (isSpacePreResting && !isDevelopment) ||
                                            item.status === 'Deleting'
                                        "
                                        small>
                                        <v-icon>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list nav dense>
                                    <application-config
                                        v-if="!isSpaceInAnyRestStateExceptPreResting"
                                        :maxNCU="currentSpaceType !== spaceTypes.EDUCATION_SPACE ? 16 : 4"
                                        :aid="item.aid"
                                        :canSave="isSpaceAdmin || (isInstanceEditor && currentSpaceType === spaceTypes.RESEARCH_SPACE)"
                                        :isTrial="isTrialSpace">
                                        <v-list-item @click="closeMenu(item)">
                                            <v-list-item-icon>
                                                <v-icon color="secondary">mdi-cog-outline</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="secondary--text mr-5">Configure</v-list-item-title>
                                        </v-list-item>
                                    </application-config>
                                    <v-list-item :to="{ name: 'app-sessions', params: { aid: item.aid } }">
                                        <v-list-item-icon>
                                            <v-icon color="secondary">mdi-timeline-clock-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="secondary--text mr-5">Sessions</v-list-item-title>
                                    </v-list-item>
                                    <template v-if="isDevelopment">
                                        <factory-reset :app="item">
                                            <v-list-item @click="closeMenu(item)">
                                                <v-list-item-icon>
                                                    <v-icon color="secondary">restart_alt</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="secondary--text mr-5">Factory reset</v-list-item-title>
                                            </v-list-item>
                                        </factory-reset>
                                        <v-list-item :to="{ query: { resetAid: item.aid } }" e2e-app-clear-settings>
                                            <v-list-item-icon>
                                                <v-icon color="secondary">auto_fix_high</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="secondary--text mr-5">Clear Settings</v-list-item-title>
                                        </v-list-item>
                                        <app-rename-dialog v-if="isSpaceAdmin" :current="item">
                                            <v-list-item @click="closeMenu(item)" e2e-app-rename>
                                                <v-list-item-icon>
                                                    <v-icon color="secondary">mdi-square-edit-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="secondary--text mr-5">Rename application</v-list-item-title>
                                            </v-list-item>
                                        </app-rename-dialog>
                                        <app-clone-btn
                                            v-if="
                                                (currentSpaceType === spaceTypes.RESEARCH_SPACE && (isSpaceAdmin || isInstanceEditor)) ||
                                                (currentSpaceType !== spaceTypes.RESEARCH_SPACE && isSpaceAdmin)
                                            "
                                            :application="item">
                                            <v-list-item @click="closeMenu(item)">
                                                <v-list-item-icon>
                                                    <v-icon color="secondary">mdi-animation-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="secondary--text mr-5">Clone</v-list-item-title>
                                            </v-list-item>
                                        </app-clone-btn>
                                        <stop-application
                                            v-if="item.status !== 'Stopped'"
                                            :app="item"
                                            :appName="item.long_id"
                                            :isShared="item.shared === 'true'">
                                            <v-list-item e2e-app-stop>
                                                <v-list-item-icon>
                                                    <v-icon color="secondary">mdi-square</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="secondary--text mr-5">Stop</v-list-item-title>
                                            </v-list-item>
                                        </stop-application>
                                        <v-list-item
                                            v-if="isSpaceAdmin && isMasterInstance"
                                            :disabled="startingAppForAll"
                                            @click="startAppForAll(item.aoid)"
                                            e2e-app-start-for-all>
                                            <v-list-item-icon>
                                                <v-progress-circular v-if="startingAppForAll" size="18" width="2" indeterminate color="secondary" />
                                                <v-icon v-else color="secondary">launch</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title :class="[startingAppForAll ? 'text--disabled' : 'secondary--text', 'mr-5']">
                                                Start for all users
                                            </v-list-item-title>
                                        </v-list-item>
                                        <app-schedule-dialog
                                            v-if="isSpaceAdmin && isMasterInstance && currentSpaceType === spaceTypes.EDUCATION_SPACE && !isSpaceArchived"
                                            :schedules="schedules"
                                            :current="item"
                                            @fetchSchedules="fetchSchedules">
                                            <v-list-item @click="closeMenu(item)">
                                                <v-list-item-icon>
                                                    <v-icon color="secondary">mdi-calendar-clock-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="secondary--text mr-5">Schedule for start</v-list-item-title>
                                            </v-list-item>
                                        </app-schedule-dialog>
                                        <v-divider v-if="isInstanceEditor && !isSpaceArchived && !isTrialSpace && item.exportable" class="my-2" />
                                        <v-list-item v-if="isInstanceEditor && !isSpaceArchived && item.exportable" @click="showDockerTagDialogForApp(item)">
                                            <v-list-item-icon>
                                                <v-icon color="secondary">mdi-export</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="secondary--text mr-5">Export application</v-list-item-title>
                                        </v-list-item>
                                        <v-divider v-if="isSpaceAdmin || isInstanceEditor" class="my-2"></v-divider>
                                    </template>
                                    <template v-if="isSpaceAdmin || isInstanceEditor">
                                        <app-delete-dialog v-if="isDevelopment" :selected="[item]" :disabled="item.status === 'Running'">
                                            <v-list-item :disabled="item.status === 'Running'" @click="closeMenu(item)" e2e-app-delete-for-me>
                                                <v-list-item-icon>
                                                    <v-icon
                                                        :color="item.status === 'Running' ? null : 'error'"
                                                        :class="item.status === 'Running' ? 'text--disabled' : ''">
                                                        mdi-trash-can-outline
                                                    </v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title :class="[item.status === 'Running' ? 'text--disabled' : 'error--text', 'mr-5']">
                                                    {{ getDeleteLabel }}
                                                </v-list-item-title>
                                            </v-list-item>
                                        </app-delete-dialog>
                                        <template v-if="currentSpaceType !== spaceTypes.RESEARCH_SPACE && isSpaceAdmin">
                                            <app-delete-dialog
                                                v-if="isMasterInstance || isDistributedInstance"
                                                :selected="[item]"
                                                :mode="2"
                                                :disabled="item.status === 'Running'">
                                                <v-list-item :disabled="item.status === 'Running'" @click="closeMenu(item)" e2e-app-delete-for-others>
                                                    <v-list-item-icon>
                                                        <v-icon
                                                            :color="item.status === 'Running' ? null : 'error'"
                                                            :class="item.status === 'Running' ? 'text--disabled' : ''">
                                                            mdi-trash-can-outline
                                                        </v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-title :class="[item.status === 'Running' ? 'text--disabled' : 'error--text', 'mr-5']">
                                                        {{ getDeleteForOthersLabel }}
                                                    </v-list-item-title>
                                                </v-list-item>
                                            </app-delete-dialog>
                                            <app-delete-dialog
                                                v-if="isDevelopment && isMasterInstance"
                                                :selected="[item]"
                                                :mode="1"
                                                :disabled="item.status === 'Running'">
                                                <v-list-item :disabled="item.status === 'Running'" @click="closeMenu(item)" e2e-app-delete-for-all>
                                                    <v-list-item-icon>
                                                        <v-icon
                                                            :color="item.status === 'Running' ? null : 'error'"
                                                            :class="item.status === 'Running' ? 'text--disabled' : ''">
                                                            mdi-trash-can-outline
                                                        </v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-title :class="[item.status === 'Running' ? 'text--disabled' : 'error--text', 'mr-5']">
                                                        Delete for all
                                                    </v-list-item-title>
                                                </v-list-item>
                                            </app-delete-dialog>
                                        </template>
                                    </template>
                                </v-list>
                            </v-menu>
                        </div>
                    </template>
                    <template v-slot:[`item.lib_usage`]="{ item }">
                        <div class="text-no-wrap">{{ item.lib_usage === null ? '' : humanFileSize(item.lib_usage) }}</div>
                    </template>
                </v-data-table>
            </template>
        </v-card-text>
        <template v-if="isSpaceAdmin && currentSpaceType === spaceTypes.EDUCATION_SPACE && isMasterInstance">
            <v-card-subtitle>Schedules</v-card-subtitle>
            <v-card-text>
                <template>
                    <v-data-table
                        item-key="ssid"
                        :headers="scheduleHeaders"
                        :items="filteredSchedules"
                        :loading="loading"
                        :options="tableOptions"
                        :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                        class="pb-12">
                        <template v-slot:top="{ pagination, options, updateOptions }">
                            <div class="d-flex align-center ml-4">
                                <v-checkbox
                                    v-model="showOldSchedules"
                                    hide-details
                                    label="Show past schedules too"
                                    :disabled="loading"
                                    class="ma-0 pa-0"></v-checkbox>
                                <v-spacer></v-spacer>
                                <v-data-footer
                                    :pagination="pagination"
                                    :options="options"
                                    @update:options="updateOptions"
                                    :itemsPerPageOptions="itemsPerPageOptions"
                                    items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                                    class="no-border" />
                            </div>
                        </template>
                        <template v-slot:no-data>
                            <span>No scheduled applications found. Consider scheduling a new application via the "Schedule for start" button in actions.</span>
                        </template>
                        <template v-slot:[`item.icon`]="{ item }">
                            <div class="d-flex">
                                <img width="30" height="30" :src="appIcon(item)" />
                            </div>
                        </template>
                        <template v-slot:[`item.long_id`]="{ item }">
                            <div v-if="isDevelopment && !isInstanceArchived" class="subtitle-2">
                                {{ item.long_id }}
                            </div>
                            <div v-else>
                                <span>{{ item.long_id }}</span>
                                <v-tooltip bottom v-if="isInstanceArchived">
                                    <template v-slot:activator="{ on }">
                                        <v-btn
                                            class="ml-1"
                                            small
                                            icon
                                            :to="{
                                                name: 'instance-snapshots',
                                                params: {
                                                    oid: $route.params.oid,
                                                    sid: $route.params.sid,
                                                    iid: $route.params.iid,
                                                    snid: $route.params.snid
                                                }
                                            }">
                                            <v-icon v-on="on">info</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>This instance is archived, please restore a snapshot first</span>
                                </v-tooltip>
                            </div>
                        </template>
                        <template v-slot:[`item.startup_time`]="{ item }">
                            {{ item.startup_time | dateTimeToHuman }}
                        </template>
                        <template v-slot:[`item.type_value`]="{ item }">
                            <v-chip small v-if="item.node_pool">{{ item.type_value }}</v-chip>
                        </template>
                        <template v-slot:[`item.stop_after_minutes`]="{ item }">
                            <div v-if="item.node_pool">{{ item.stop_after_minutes }}</div>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                            <div class="d-flex flex-nowrap justify-end align-center">
                                <v-btn
                                    :disabled="isSpaceArchived || disableScheduleNextWeek(item) || loading"
                                    outlined
                                    small
                                    @click="setScheduleNextWeek(item.aoid, item.startup_time, item?.node_pool, item?.stop_after_minutes)"
                                    text
                                    class="showOnHover mr-2">
                                    <v-icon left small>navigate_next</v-icon>
                                    Add to next week
                                </v-btn>
                                <v-menu v-model="dropdownMenu[`schedule_${item.ssid}`]" left offset-x nudge-top="10">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" text color="secondary" :disabled="loading" small>
                                            <v-icon>more_horiz</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list nav dense>
                                        <app-schedule-dialog
                                            :schedules="schedules"
                                            v-if="!item.past && !loading"
                                            :current="item"
                                            crud="update"
                                            @fetchSchedules="fetchSchedules">
                                            <v-list-item @click="dropdownMenu[`schedule_${item.ssid}`] = false">
                                                <v-list-item-icon>
                                                    <v-icon color="secondary">mdi-pencil-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="secondary--text mr-5">Edit</v-list-item-title>
                                            </v-list-item>
                                        </app-schedule-dialog>

                                        <v-divider class="my-2"></v-divider>

                                        <v-list-item
                                            :disabled="loading"
                                            @click="
                                                deleteSchedule(item.ssid)
                                                dropdownMenu[`schedule_${item.ssid}`] = false
                                            ">
                                            <v-list-item-icon>
                                                <v-icon :color="loading ? null : 'error'" :class="loading ? 'text--disabled' : ''">
                                                    mdi-trash-can-outline
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title :class="[loading ? 'text--disabled' : 'error--text', 'mr-5']">Delete</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </div>
                        </template>
                    </v-data-table>
                </template>
            </v-card-text>
        </template>
        <ResetApplicationDialog />
        <v-dialog v-model="dockerExportTagDialog" max-width="600px" lazy-validation persistent>
            <v-card>
                <v-card-title class="flex-nowrap align-start">
                    Choose an optional tag for your app
                    <v-spacer></v-spacer>
                    <v-btn @click="dockerExportTagDialog = false" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-subtitle class="caption">A random tag will be generated if none given.</v-card-subtitle>
                <v-card-text class="mb-0">
                    <v-form v-model="dockerExportTagIsValid" @submit.prevent="exportToImage()">
                        <v-text-field
                            v-model="dockerExportTag"
                            outlined
                            hint="Lower case letters, numbers, dashes and underscores only"
                            :rules="[rules.validChars]"
                            dense>
                            <template v-slot:label>Type a tag here</template>
                        </v-text-field>
                    </v-form>
                </v-card-text>
                <v-card-text v-if="isRunning">
                    <v-alert prominent type="warning" class="my-0" text>
                        We recommend stopping your application before exporting to prevent possible inconsitencies.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-checkbox v-model="skipFileExport">
                        <template v-slot:label>
                            <span class="caption">Do not export workspace files</span>
                        </template>
                    </v-checkbox>
                    <v-spacer></v-spacer>

                    <v-btn text color="primary" @click="exportToImage()" :disabled="!dockerExportTagIsValid">Export</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="wakeSpaceDialog" width="500" v-if="isSpaceRested" persistent>
            <v-card>
                <v-card-title class="flex-nowrap align-start">
                    Wake up space
                    <v-spacer></v-spacer>
                    <v-btn @click="wakeSpaceDialog = false" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="pt-3">
                    <div>
                        Before the application can be started, the space will be removed from resting state. This can take a couple of minutes depending on the
                        total size of the storage in this space.
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn color="primary" text @click="wakeSpace()">Continue</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { appTypeAndImageLink } from '@/mixins/appTypeAndImage'
import { enumsData } from '@/mixins/enums'
import { humanFileSize, dateTimeConcat } from '@/utils'
import { fetchAppStartupSchedules, setAppStartupSchedule, deleteAppStartupSchedule, fetchTask } from '@/apis'
import { DateTime } from 'luxon'
import { scaling } from '@/mixins/scaling'
import { pushUiMixin } from '@/mixins/pushUi'
import { deploymentState, deploymentStates, deploymentHasFailure, deploymentStatusInfo } from '@/modules/application/deploymentStatus'

const CopyToClipboard = () => import('@/components/CopyToClipboard')
const StopApplication = () => import('@/modules/application/components/TheApplicationStopDialog.vue')
const ApplicationConfig = () => import('@/modules/application/components/ApplicationConfigDialog.vue')
const ResetApplicationDialog = () => import('@/modules/application/components/ResetApplicationDialog')
const AppScheduleDialog = () => import('./AppScheduleDialog.vue')
const AppDeleteDialog = () => import('./AppDeleteDialog.vue')
const AppRenameDialog = () => import('./AppRenameDialog.vue')
const FactoryReset = () => import('./FactoryReset.vue')
const AppCloneBtn = () => import('./AppCloneBtn.vue')
const VersionDescriptionViewer = () => import('../components/VersionDescriptionViewer.vue')
const AppScaleInfoMenu = () => import('@/components/AppScaleInfoMenu.vue')
const RestedCard = () => import('@/modules/snapshot/components/RestedCard.vue')

export default {
    name: 'AppList',
    mixins: [appTypeAndImageLink, enumsData, scaling, pushUiMixin],
    components: {
        ApplicationConfig,
        CopyToClipboard,
        ResetApplicationDialog,
        StopApplication,
        AppScheduleDialog,
        AppRenameDialog,
        FactoryReset,
        VersionDescriptionViewer,
        AppCloneBtn,
        AppDeleteDialog,
        AppScaleInfoMenu,
        RestedCard
    },
    data() {
        return {
            search: '',
            schedules: [],
            headers: [
                { text: 'Type', align: 'left', value: 'icon', sortable: false, width: '30px' },
                { text: 'Name', align: 'left', value: 'long_id', class: 'pl-8' },
                { text: 'Size', align: 'left', value: 'size', class: 'pl-8' },
                { text: 'Credit/hour', align: 'left', value: 'active_resource' },
                { text: 'Status', value: 'status', align: 'left' },
                { text: 'Description', align: 'left', value: 'description' },
                { text: 'Space usage', align: 'left', value: 'lib_usage' },
                { text: 'Actions', align: 'right', value: 'actions', sortable: false }
            ],
            scheduleHeaders: [
                { text: 'Type', align: 'left', value: 'icon', width: '30px' },
                { text: 'Name', align: 'left', value: 'long_id' },
                { text: 'Date', align: 'left', value: 'startup_time' },
                { text: 'Custom resources', align: 'left', value: 'type_value' },
                { text: 'Session length [min]', align: 'left', value: 'stop_after_minutes' },
                { text: 'Actions', align: 'right', value: 'actions', sortable: false }
            ],
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            },
            error: false,
            errorContent: '',
            selected: [],
            AddAppdialog: false,
            startingAppForAll: false,
            showOldSchedules: false,
            loading: false,
            dropdownMenu: {},
            exportingApp: null,
            dockerExportTag: '',
            skipFileExport: false,
            dockerExportTagDialog: false,
            dockerExportTagIsValid: true,
            rules: {
                validChars: p => /^[a-z0-9_-]*$/g.test(p) || 'Please use only lower case letters, numbers and dashes.'
            },
            envs: {},
            precision: this.$appConfig?.VUE_APP_DASHBOARD_PRECISION,
            wakeSpaceDialog: false,
            wakeSpaceAppId: null
        }
    },
    created() {
        this.fetchSchedules()
        this.$store.dispatch('appStore/fetchNodePools')
        this.$store.dispatch('appStore/fetchLocalNodePools')
        this.$store.dispatch('snapshotStore/fetchApplications', this.$route.params.snid)
        if (this.$route.query.search) this.search = this.$route.query.search
    },
    computed: {
        applicationsWithStatus() {
            return this.applications.map(app => {
                if (app.enabled_flag === 0 && app.delete_timestamp === null) {
                    app.status = 'Deleting'
                } else {
                    app.status = 'Stopped'
                }
                const appDeployment = this.deploymentStatus.find(deployment => {
                    return deployment.aid.toString() === app.aid.toString()
                })

                if (appDeployment) {
                    const state = deploymentState(appDeployment)
                    let status = {
                        [deploymentStates.RUNNING]: 'Running',
                        [deploymentStates.STARTING]: 'Starting',
                        [deploymentStates.STOPPING]: 'Stopping',
                        [deploymentStates.UNAVAILABLE]: 'Unavailable'
                    }[state]
                    // Failure affordances only when push UI is live (new BE path).
                    if (this.pushUiEnabled && deploymentHasFailure(appDeployment) && state !== deploymentStates.RUNNING) {
                        status = 'Unavailable'
                    } else if (!this.pushUiEnabled && status === 'Unavailable') {
                        // Old FE treated non-running active deploys as Starting.
                        status = 'Starting'
                    }
                    app.status = status
                    if (this.pushUiEnabled) {
                        app.failure_reason = appDeployment.failure_reason || null
                        app.failure_message = appDeployment.failure_message || null
                        app.failure_container = appDeployment.failure_container || null
                        app.restart_count = Number(appDeployment.restart_count) || 0
                        app.statusInfo = deploymentStatusInfo(appDeployment)
                    } else {
                        app.failure_reason = null
                        app.failure_message = null
                        app.failure_container = null
                        app.restart_count = 0
                        app.statusInfo = null
                    }
                    app.shared = appDeployment.shared
                    app.active_resource_runtime = appDeployment.active_resource_runtime
                } else {
                    app.statusInfo = null
                    app.failure_reason = null
                    app.failure_message = null
                    app.failure_container = null
                    app.restart_count = 0
                    const last = this.lastStopReasonByAid && this.lastStopReasonByAid[String(app.aid)]
                    if (this.pushUiEnabled && app.status === 'Stopped' && last && last.reason) {
                        app.statusInfo = {
                            state: 'STOPPED',
                            label: `Stopped due to ${last.reason}`,
                            color: 'error',
                            pulse: false
                        }
                        app.failure_reason = last.reason
                        app.failure_message = last.message || null
                    }
                }
                return app
            })
        },
        currentURL() {
            return window.location.href
        },
        headersFiltered: function () {
            const snapshotHeaders = ['Type', 'Name', 'Description', 'Actions']
            return this.isDevelopment
                ? this.headers
                : this.headers.filter(c => {
                      return snapshotHeaders.indexOf(c.text) >= 0
                  })
        },
        filteredSchedules() {
            if (this.showOldSchedules) {
                return this.schedules
            } else {
                return this.schedules.filter(schedule => !schedule.past)
            }
        },
        isRunning() {
            if (!this.exportingApp?.aid) return false
            return this.runningDeployments.find(deployment => {
                return deployment.aid.toString() === this.exportingApp.aid.toString()
            })
        },
        ...mapState(['userInfo']),
        ...mapGetters('appStore', ['runningDeployments']),
        ...mapState('appStore', ['deploymentStatus', 'lastStopReasonByAid']),
        ...mapGetters('snapshotStore', ['isDevelopment']),
        ...mapState('snapshotStore', ['stagingObjects', 'applications', 'applicationsFetching']),
        ...mapState(['userMetadata', 'fetchingUserInfo']),
        ...mapState('spaceStore', ['currentSpace']),
        ...mapGetters('spaceStore', [
            'isSpaceAdmin',
            'currentSpaceType',
            'isSpaceArchived',
            'privateInstances',
            'isTrialSpace',
            'isSpaceRested',
            'isSpaceResting',
            'isSpacePreResting',
            'isSpaceInAnyRestState',
            'isSpaceInAnyRestStateExceptPreResting'
        ]),
        ...mapGetters('instanceStore', ['isInstanceEditor', 'isMasterInstance', 'isInstanceArchived', 'isDistributedInstance']),
        getDeleteForOthersLabel() {
            return this.isDistributedInstance && this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE ? 'Delete for students' : 'Delete for others'
        },
        getDeleteLabel() {
            if (this.currentSpaceType === this.spaceTypes.RESEARCH_SPACE) {
                return 'Delete'
            } else {
                if (this.isSpaceAdmin && this.isMasterInstance) {
                    return 'Delete for me'
                } else {
                    return 'Delete'
                }
            }
        },
        isOndemandGPUForStudents() {
            return (
                (this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE &&
                    this.currentSpace.space_configs?.on_demand_gpu_for_students &&
                    this.isDevelopment) ||
                false
            )
        },
        switchCloudEnabled() {
            return this.isSwitchCloudEnabled()
        }
    },
    methods: {
        async exportToImage() {
            if (!this.dockerExportTagIsValid) return

            this.dockerExportTagDialog = false
            const body = {}
            if (this.dockerExportTag) body.image_name = this.dockerExportTag
            if (this.skipFileExport) body.export_files = false
            try {
                await this.$axios.post(`/applications/${this.exportingApp.aid}/export`, body)

                this.$store.dispatch('showSnackBar', {
                    snackBarText: `Successfully started exporting ${this.exportingApp.long_id}. You will be notified in email when it is available to use.`,
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
                this.$store.dispatch('userStore/fetchRunningUserTasks')
            } catch (error) {
                console.error(error)
                this.$store.dispatch('showSnackBar', {
                    snackBarText: `There was an error while starting export. Please try again later.`,
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
        },
        removeSearchQuery() {
            this.$router.replace({
                ...this.$router.currentRoute,
                query: {
                    search: undefined
                }
            })
        },
        addToStaging: function (data) {
            if (data.length) {
                const stagingObjectsBefore = this.stagingObjects.applications.length
                const filesWithPaths = []
                let fileOrder = data.length
                data.forEach(file => {
                    const fileObject = file
                    fileObject.filePath = file.long_id
                    fileObject.fileType = this.nuvolosObjectTypes.APPLICATION
                    filesWithPaths.push(fileObject)
                    fileOrder = fileOrder - 1
                    if (fileOrder === 0) {
                        this.$store.dispatch('snapshotStore/updateStagingObjects', {
                            itemType: this.nuvolosObjectTypes.APPLICATION,
                            newItems: filesWithPaths,
                            updateMode: 'add'
                        })
                        this.$store.dispatch('showSnackBar', {
                            snackBarText: 'Applications added to stage.',
                            snackBarTimeout: 5000,
                            snackBarIcon: 'check_circle'
                        })
                        this.selected = []
                    }
                    const stagingObjectsAfter = this.stagingObjects.applications.length
                    if (stagingObjectsAfter !== stagingObjectsBefore) {
                        this.$store.dispatch('showSnackBar', {
                            snackBarText: 'Applications added to stage.',
                            snackBarTimeout: 5000,
                            snackBarIcon: 'check_circle'
                        })
                    } else {
                        this.$store.dispatch('showSnackBar', {
                            snackBarText: 'Selected applications already staged.',
                            snackBarTimeout: 10000,
                            snackBarIcon: 'info'
                        })
                    }
                })
            }
        },
        startAppForAll(aoid) {
            this.startingAppForAll = true
            const postBody = {
                aoids: [aoid],
                explicit_editors_only: false,
                wait_secs: 6,
                max_users: 150
            }
            this.$axios
                .post(`/spaces/${this.$route.params.sid}/start_apps`, postBody)
                .then(response => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'User applications have been started!',
                        snackBarTimeout: 5000,
                        snackBarIcon: 'check_circle'
                    })
                })
                .catch(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'Failed to start the applications',
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                })
                .finally(() => {
                    this.startingAppForAll = false
                })
        },
        errorMessage(error) {
            if (error) {
                this.error = true
                this.errorContent = 'An error has occurred'
            }
        },
        openApp(aid) {
            if (this.isSpaceRested) {
                this.wakeSpaceDialog = true
                this.wakeSpaceAppId = aid
                return
            }

            this.$router.push({
                name: 'app-open',
                params: {
                    oid: this.$route.params.oid,
                    sid: this.$route.params.sid,
                    iid: this.$route.params.iid,
                    snid: this.$route.params.snid,
                    aid
                }
            })
        },
        async wakeSpace() {
            try {
                const data = await this.$store.dispatch('appStore/startApp', { aid: this.wakeSpaceAppId, dontWaitTask: true })
                this.$store.dispatch('spaceStore/fetchCurrentSpace')
                this.wakeSpaceDialog = false
                const taskResult = await fetchTask(data.tkid)
                if (taskResult instanceof Error) {
                    throw taskResult
                } else {
                    await this.$store.dispatch('snapshotStore/fetchApplications', this.$route.params.snid)
                    this.$store.dispatch('spaceStore/fetchCurrentSpace')
                    this.openApp(taskResult.data.meta_data.aid)
                }
            } catch (error) {
                this.$store.dispatch('spaceStore/fetchCurrentSpace')
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to wake up space.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
        },
        humanFileSize(bytes, si) {
            return humanFileSize(bytes, si)
        },
        async fetchSchedules() {
            this.loading = true
            try {
                const { data } = await fetchAppStartupSchedules({ sid: this.$route.params.sid, all: true })
                const now = DateTime.now()
                data.forEach(schedule => {
                    const dt = DateTime.fromISO(schedule.startup_time)
                    if (dt < now) {
                        schedule.past = true
                    }
                    schedule.dateForInput = dt.toFormat(this.$appConfig?.VUE_APP_DATETIMEFORMAT)
                })
                this.schedules = data
            } catch (error) {
                console.log(error)
            } finally {
                this.loading = false
            }
        },
        async setScheduleNextWeek(aoid, dateTime, nodePool, stopAfterMinutes) {
            this.loading = true
            const dt = DateTime.fromISO(dateTime)
            const nextWeek = dt.plus({ days: 7 })
            try {
                await setAppStartupSchedule({
                    sid: this.$route.params.sid,
                    aoid,
                    startupTime: dateTimeConcat(nextWeek.toISODate(), nextWeek.toFormat('T')),
                    nodePool: nodePool || null,
                    stopAfterMinutes
                })
                this.loading = false
                this.fetchSchedules()
            } catch (error) {
                this.loading = false
                console.log(error)
            }
        },
        async deleteSchedule(ssid) {
            this.loading = true
            try {
                await deleteAppStartupSchedule(ssid)
                this.loading = false
                this.fetchSchedules()
            } catch (error) {
                this.loading = false
                console.log(error)
            }
        },
        disableScheduleNextWeek(schedule) {
            const dt = DateTime.fromISO(schedule.startup_time)
            const now = DateTime.now()
            return dt.plus({ days: 7 }) < now
        },
        closeMenu(app) {
            this.dropdownMenu[`app_${app.aid}`] = false
        },
        showDockerTagDialogForApp(app) {
            this.exportingApp = app
            this.dockerExportTagDialog = true
        },
        containsDeletingApps(apps) {
            return apps.some(app => app.status === 'Deleting')
        },
        getCurrentSwitchSelection(app) {
            if (!app || app.active_resource === 'ncu') return null
            const nodepool = this.currentLocalNodePool(app.node_pool)
            if (!nodepool || nodepool.cloud !== 'switch') return null
            return nodepool
        },
        getSwitchSelectionFamily(selection) {
            if (!selection) return 'cpu'
            if (selection.gpu_type) return 'gpu'
            if (selection.ssd) return 'memory'
            if (selection.cpu && selection.memory && Number(selection.memory) / Number(selection.cpu) > 4) return 'memory'
            return 'cpu'
        },
        getScaleRoute(app) {
            if (this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE && !this.isSpaceAdmin) return ''

            const route = {
                name: 'application-scale',
                params: { aid: app.aid }
            }

            const switchSelection = this.switchCloudEnabled ? this.getCurrentSwitchSelection(app) : null
            if (switchSelection) {
                route.query = {
                    tab: 'switch',
                    switchFamily: this.getSwitchSelectionFamily(switchSelection)
                }
            }

            return route
        },
        refresh() {
            this.$store.dispatch('snapshotStore/fetchApplications', this.$route.params.snid)
            if (this.isOndemandGPUForStudents) {
                this.$store.dispatch('instanceStore/fetchInstanceUsage', this.$route.params.iid)
            }
        }
    },
    watch: {
        applicationsWithStatus(nextValue, preValue) {
            if (nextValue.length < preValue.length) {
                if (this.selected.length === 1) {
                    this.selected = []
                }
            }
        }
    }
}
</script>

<style scoped>
.status-pulse {
    animation: statusPulse 1.2s ease-in-out infinite;
}
@keyframes statusPulse {
    0%,
    100% {
        opacity: 0.55;
    }
    50% {
        opacity: 1;
    }
}
@media (prefers-reduced-motion: reduce) {
    .status-pulse {
        animation: none;
    }
}
</style>
